if (!customElements.get('product-info')) {
  customElements.define('product-info', class ProductInfo extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.quantity__input');
      this.currentVariant = this.querySelector('.product-variant-id');
      this.variantSelects = this.querySelector('variant-radios');
      this.submitButton = this.querySelector('[type="submit"]');
      this.sectionId = this.dataset.section;
      this.originalSectionId = this.dataset.originalSection || this.sectionId;
    }

    cartUpdateUnsubscriber = undefined;
    variantChangeUnsubscriber = undefined;

    connectedCallback() {
      if (!this.input) return;
      this.quantityForm = this.querySelector('.product-form__quantity');
      if (!this.quantityForm) return;
      this.calculateQuantity();
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.fetchQuantityRules();
      });
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data.sectionId !== this.originalSectionId) return;
        this.updateQuantityRules(event.data.sectionId, event.data.html);
        this.calculateQuantity();
      });

      this.showStickyBar();
      this.input.addEventListener('input', this.calculateQuantity.bind(this));
      this.input.addEventListener('change', this.calculateQuantity.bind(this));
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }
    }

    showStickyBar() {
      if (this.dataset.originalSection) return;
      const buyBlock = document.querySelector('.buy-block-wrapper-main');
        if (!buyBlock) return;

        const body = document.body;
        const observer = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) {
            body.classList.add('buy-block-in-view');
            body.classList.remove('buy-block-out-of-view');
          } else {
            body.classList.remove('buy-block-in-view');
            body.classList.add('buy-block-out-of-view');
          }
        }, { threshold: 0 });

        observer.observe(buyBlock);
    }

    calculateQuantity() {
      if (!this.input) return;
      const inputData = this.input.dataset;
      const cartQty = inputData.cartQuantity ? parseInt(inputData.cartQuantity, 10) : 0;
      const hardMaxVal = inputData.max ? parseInt(inputData.max, 10) : 0;
      const hardMax = hardMaxVal > 0 ? hardMaxVal : null;
      const baseMin = inputData.min ? parseInt(inputData.min, 10) : 1;
      const step = this.input.step ? parseInt(this.input.step, 10) : 1;
      const typed = parseInt(this.input.value, 10);
      const remaining = hardMax != null ? (hardMax - cartQty) : null;
      const rulesContainer = this._rulesContainer || (this._rulesContainer = this.quantityForm?.querySelector('[data-max-per-order-message]'));

      if (remaining !== null && remaining <= 0) {
        this.input.min = 0;
        this.input.max = 0;
        this.input.value = 0;
        this.disableAddToCart();
        this.showPersistentMaxMessage(rulesContainer, hardMax);
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
        return;
      }

      let effectiveMin = baseMin;
      if (remaining !== null) effectiveMin = Math.min(effectiveMin, remaining);
      if (cartQty >= baseMin) effectiveMin = Math.min(effectiveMin, step);

      let validatedQuantity = isNaN(typed) ? effectiveMin : typed;
      if (remaining !== null && validatedQuantity > remaining) validatedQuantity = remaining;
      if (validatedQuantity < effectiveMin) validatedQuantity = effectiveMin;

      this.input.min = effectiveMin;
      if (remaining !== null) this.input.max = remaining; else this.input.removeAttribute('max');
      this.input.value = validatedQuantity;
      this.enableAddToCart();

      if (rulesContainer && (remaining === null || remaining > 0)) {
        rulesContainer.classList.add('hidden');
        rulesContainer.classList.remove('is-active');
      }
      publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
    }

    showPersistentMaxMessage(rulesContainer, limit) {
      if (!rulesContainer) return;
      const template = rulesContainer.getAttribute('data-max-text');
      const message = template ? template.replace('__Q__', limit) : `Max ${limit} per order`;
      rulesContainer.textContent = message;
      rulesContainer.classList.remove('hidden');
      rulesContainer.classList.add('is-active');
    }

    fetchQuantityRules() {
      if (!this.currentVariant || !this.currentVariant.value) return;
      const overlay = this.querySelector('.quantity__rules-cart .loading-overlay');
      overlay?.classList.remove('hidden');
      fetch(`${this.dataset.url}?variant=${this.currentVariant.value}&section_id=${this.originalSectionId}`).then((response) => {
        return response.text()
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateQuantityRules(this.originalSectionId, html);
        this.calculateQuantity();
      })
      .catch(e => {
        console.error(e);
      })
      .finally(() => {
        overlay?.classList.add('hidden');
      });
    }

    updateQuantityRules(sectionId, html) {
      const targetFormId = this.quantityForm?.id || `Quantity-Form-${sectionId}`;
      const quantityFormUpdated = html.getElementById(targetFormId);
      if (!quantityFormUpdated) return;
      const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
      for (let selector of selectors) { 
        const current = this.quantityForm.querySelector(selector);
        const updated = quantityFormUpdated.querySelector(selector);
        if (!current || !updated) continue;
        if (selector === '.quantity__input') {
          const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
          for (let attribute of attributes) {
            const valueUpdated = updated.getAttribute(attribute);
            if (valueUpdated !== null) current.setAttribute(attribute, valueUpdated);
          }
        } else {
          current.innerHTML = updated.innerHTML;
        }
      }
    }

    disableAddToCart() {
      if (!this.submitButton) return;
      if (this.submitButton.dataset.soldOut === 'true') return;
      this.submitButton.setAttribute('disabled', 'disabled');
      this.submitButton.setAttribute('aria-disabled', 'true');
    }

    enableAddToCart() {
      if (!this.submitButton || this.submitButton.dataset.soldOut === 'true') return;

      const soldOutText = window.variantStrings?.soldOut;
      const inner =
        this.submitButton.querySelector('.product-form__submit-inner') ||
        this.submitButton.querySelector('span');
      const label = (inner?.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (soldOutText && label.includes(soldOutText.trim().toLowerCase())) return;

      this.submitButton.removeAttribute('disabled');
      this.submitButton.removeAttribute('aria-disabled');
    }
  }
)};

if (!customElements.get("pdp-sticky-cta")) {
  class PdpStickyCta extends HTMLElement {
    connectedCallback() {
      this.triggerSelector = this.dataset.triggerSelector || "#add-to-cart";
      this.productFormSelector = this.dataset.productFormSelector || "#product-form";
      this.addToCartText = this.dataset.addToCartText || "Add to Cart";
      this.selectOptionsText = this.dataset.selectOptionsText || "Select options";
      this.soldOutText =
        window.variantStrings?.soldOut || this.dataset.soldOutText || "Sold out";
      this.unavailableText = window.variantStrings?.unavailable || "Unavailable";
      this.sectionId = this.dataset.sectionId;

      this.button = this.querySelector("[data-sticky-cta-button]");
      this.buttonLabel = this.querySelector("[data-sticky-cta-button-label]");
      this.priceEl = this.querySelector("[data-sticky-cta-price]");
      this.sepEl = this.querySelector("[data-sticky-cta-sep]");
      this.iconEl = this.querySelector("[data-sticky-cta-icon]");

      this.onScroll = this.onScroll.bind(this);
      this.onButtonClick = this.onButtonClick.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onProductChange = this.onProductChange.bind(this);

      this.button?.addEventListener("click", this.onButtonClick);
      window.addEventListener("scroll", this.onScroll, { passive: true });
      window.addEventListener("resize", this.onScroll, { passive: true });

      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.onVariantChange
        );
      }

      requestAnimationFrame(() => {
        this.resolveTargets();
        this.observeMainPrice();
        this.observeMainSubmit();
        this.onScroll();
        this.syncButtonState();
      });
    }

    disconnectedCallback() {
      window.removeEventListener("scroll", this.onScroll);
      window.removeEventListener("resize", this.onScroll);
      this.button?.removeEventListener("click", this.onButtonClick);
      this.productRoot?.removeEventListener("change", this.onProductChange);
      if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      if (this.priceObserver) this.priceObserver.disconnect();
      if (this.submitObserver) this.submitObserver.disconnect();
    }

    resolveTargets() {
      this.triggerEl =
        document.querySelector(this.triggerSelector) ||
        document.querySelector(`#ProductSubmitButton-${this.sectionId}`) ||
        document.querySelector(`#product-form-${this.sectionId} [name="add"]`) ||
        document.querySelector("#add-to-cart") ||
        document.querySelector(".buy-block-wrapper-main") ||
        document.querySelector("product-form .product-form__submit");

      this.productFormEl =
        document.querySelector(this.productFormSelector) ||
        document.querySelector(`#product-form-${this.sectionId}`) ||
        document.querySelector("product-form");

      this.productRoot =
        document.getElementById(`MainProduct-${this.sectionId}`) ||
        document.querySelector(".section-main-product");

      this.productRoot?.removeEventListener("change", this.onProductChange);
      this.productRoot?.addEventListener("change", this.onProductChange);

      if (this.triggerEl && "IntersectionObserver" in window) {
        if (this.intersectionObserver) this.intersectionObserver.disconnect();
        this.useIntersectionObserver = true;
        this.intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            const scrolledPast =
              !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.setVisible(scrolledPast);
          },
          { threshold: 0, rootMargin: "0px" }
        );
        this.intersectionObserver.observe(this.triggerEl);
      } else {
        this.useIntersectionObserver = false;
      }
    }

    getMainSubmit() {
      return (
        document.querySelector(
          `#product-form-${this.sectionId} [name="add"]`
        ) ||
        document.getElementById(`ProductSubmitButton-${this.sectionId}`) ||
        this.productRoot?.querySelector("product-form .product-form__submit") ||
        document.querySelector("product-form .product-form__submit")
      );
    }

    getVariantPicker() {
      return this.productRoot?.querySelector("variant-selects, variant-radios");
    }

    normalizeLabel(text) {
      return (text || "").replace(/\s+/g, " ").trim().toLowerCase();
    }

    labelLooksUnavailable(text) {
      const label = this.normalizeLabel(text);
      if (!label) return false;

      const soldOut = this.normalizeLabel(
        window.variantStrings?.soldOut || this.soldOutText
      );
      const unavailable = this.normalizeLabel(
        window.variantStrings?.unavailable || this.unavailableText
      );

      return (
        (soldOut && label.includes(soldOut)) ||
        (unavailable && label.includes(unavailable))
      );
    }

    observeMainPrice() {
      const mainPrice = document.getElementById(
        `buy-button-price-${this.sectionId}`
      );
      if (!mainPrice || !this.priceEl) return;

      if (this.priceObserver) this.priceObserver.disconnect();

      this.syncPriceFromMain(mainPrice);
      this.priceObserver = new MutationObserver(() => {
        this.syncPriceFromMain(mainPrice);
      });
      this.priceObserver.observe(mainPrice, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    observeMainSubmit() {
      const mainSubmit = this.getMainSubmit();
      if (!mainSubmit) return;

      if (this.submitObserver) this.submitObserver.disconnect();
      this.submitObserver = new MutationObserver(() => {
        this.syncButtonState();
      });
      this.submitObserver.observe(mainSubmit, {
        attributes: true,
        attributeFilter: ["disabled", "aria-disabled", "class"],
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    syncPriceFromMain(mainPrice) {
      if (!this.priceEl || !mainPrice) return;
      this.priceEl.innerHTML = mainPrice.innerHTML;
    }

    onProductChange() {
      const picker = this.getVariantPicker();
      if (picker) {
        this.variantAvailable = picker.currentVariant
          ? picker.currentVariant.available !== false
          : false;
      }

      requestAnimationFrame(() => this.syncButtonState());
    }

    onScroll() {
      if (this.useIntersectionObserver) return;

      if (!this.triggerEl) {
        this.triggerEl =
          document.querySelector(this.triggerSelector) ||
          document.querySelector(`#ProductSubmitButton-${this.sectionId}`) ||
          document.querySelector("#add-to-cart") ||
          document.querySelector(".buy-block-wrapper-main");
      }

      if (!this.triggerEl) {
        this.setVisible(window.scrollY > 200);
        return;
      }

      const triggerTop =
        this.triggerEl.getBoundingClientRect().top + window.scrollY;
      const scrolledPast = window.scrollY > triggerTop;
      this.setVisible(scrolledPast);
    }

    setVisible(visible) {
      if (this._visible === visible) return;
      this._visible = visible;

      if (visible) {
        this.removeAttribute("hidden");
        this.classList.add("is-visible");
        document.documentElement.style.setProperty(
          "--pdp-sticky-cta-height",
          `${this.offsetHeight}px`
        );
      } else {
        this.classList.remove("is-visible");
        this.setAttribute("hidden", "");
        document.documentElement.style.setProperty("--pdp-sticky-cta-height", "0px");
      }
    }

    onButtonClick(event) {
      event.preventDefault();
      if (this.button?.disabled || this.button?.dataset.mode === "sold-out") return;
      this.addToCart();
    }

    addToCart() {
      const mainSubmit = this.getMainSubmit();
      if (!mainSubmit) return;
      if (mainSubmit.disabled || mainSubmit.getAttribute("aria-disabled") === "true") {
        return;
      }
      if (this.labelLooksUnavailable(mainSubmit.textContent)) return;
      mainSubmit.click();
    }

    syncButtonState() {
      if (!this.button || !this.buttonLabel) return;

      const mainSubmit = this.getMainSubmit();
      const soldOutText = window.variantStrings?.soldOut || this.soldOutText;
      const isLoading = mainSubmit?.classList.contains("loading");
      const labelEl =
        mainSubmit?.querySelector(".product-form__submit-inner") ||
        mainSubmit?.querySelector("span");
      const isUnavailable = Boolean(
        this.variantAvailable === false ||
          this.labelLooksUnavailable(labelEl?.textContent) ||
          this.labelLooksUnavailable(mainSubmit?.textContent) ||
          (!isLoading &&
            (!mainSubmit ||
              mainSubmit.disabled ||
              mainSubmit.hasAttribute("disabled")))
      );

      if (isUnavailable) {
        this.button.dataset.mode = "sold-out";
        this.buttonLabel.textContent = soldOutText;
        this.button.setAttribute("disabled", "disabled");
        return;
      }

      this.button.dataset.mode = "add-to-cart";
      this.buttonLabel.textContent = this.addToCartText;
      this.button.removeAttribute("disabled");
    }

    onVariantChange(event) {
      if (!event?.data) return;
      if (event.data.sectionId && event.data.sectionId !== this.sectionId) return;

      const { html, variant } = event.data;

      if (variant) {
        this.variantAvailable = variant.available !== false;
      } else {
        this.variantAvailable = false;
      }

      const updatedButton = html?.getElementById(
        `ProductSubmitButton-${this.sectionId}`
      );
      if (updatedButton?.hasAttribute("disabled")) {
        this.variantAvailable = false;
      }

      if (html && this.priceEl) {
        const sourcePrice =
          html.getElementById(`pdp-sticky-cta-price-${this.sectionId}`) ||
          html.getElementById(`buy-button-price-${this.sectionId}`) ||
          html.getElementById(`price-${this.sectionId}`);

        if (sourcePrice) {
          this.priceEl.innerHTML = sourcePrice.innerHTML;
        }
      }

      this.observeMainSubmit();
      this.syncButtonState();
    }
  }

  customElements.define("pdp-sticky-cta", PdpStickyCta);
}

